/**
 * Wave 4 TEST economy chrome — OfflineBanner + Badge when tenant is pre-go-live.
 * Mounted from AdminPwaLifecycle (plan TEST chrome mounts).
 *
 * Publishes `--admin-test-economy-chrome-height` so EnterprisePageHeader / shell
 * reserve space and never overlap page titles on any viewport.
 */
import { createPortal } from 'react-dom';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { getTenantCodeFromPath } from 'pi-kiosk-shared';
import { fetchTenantOrgIdentity } from '../../features/account/api/account.gateway.js';
import { buildOnboardingHubJumpUrl } from '../../features/account/utils/onboardingHubJump.js';
import { shouldHideAdminChrome } from '../../shared/navigation/shouldHideAdminChrome.js';
import { Badge, OfflineBanner } from '../../shared/ui/index.js';
import { isAdminDevPwaPath } from './adminPwaSurfaces.js';
import { ADMIN_PWA_LIFECYCLE_CHROME_Z_INDEX } from './adminPwaLifecycleChrome.js';

const TEST_ECONOMY_HEIGHT_VAR = '--admin-test-economy-chrome-height';

async function loadLivePublishedAt(): Promise<string | null | undefined> {
  try {
    const response = await fetchTenantOrgIdentity();
    if (!response.success) {
      return undefined;
    }
    return response.data.livePublishedAt ?? null;
  } catch {
    return undefined;
  }
}

/**
 * Shows when `livePublishedAt` is null on the current tenant route.
 * Hidden when live, on auth/platform/dev paths, or when org-identity cannot be read.
 */
export function AdminTestEconomyChrome(): JSX.Element | null {
  const { t } = useTranslation('admin');
  const location = useLocation();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const tenantCode = getTenantCodeFromPath(location.pathname);
  const enabled =
    tenantCode != null &&
    !shouldHideAdminChrome(location.pathname) &&
    !isAdminDevPwaPath(location.pathname);

  const { data: livePublishedAt } = useSWR(
    enabled ? (['admin-test-economy-live', tenantCode] as const) : null,
    loadLivePublishedAt,
    {
      revalidateOnFocus: true,
      dedupingInterval: 30_000,
      shouldRetryOnError: false,
    },
  );

  const visible =
    enabled && livePublishedAt !== undefined && livePublishedAt == null;

  useEffect(() => {
    if (!visible) {
      document.documentElement.style.removeProperty(TEST_ECONOMY_HEIGHT_VAR);
      return;
    }
    const el = wrapRef.current;
    if (el == null) {
      return;
    }
    const publish = (): void => {
      const height = Math.ceil(el.getBoundingClientRect().height);
      document.documentElement.style.setProperty(TEST_ECONOMY_HEIGHT_VAR, `${height}px`);
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty(TEST_ECONOMY_HEIGHT_VAR);
    };
  }, [visible]);

  if (!visible) {
    return null;
  }

  if (typeof document === 'undefined') {
    return null;
  }

  const goLiveHref =
    tenantCode != null ? buildOnboardingHubJumpUrl(tenantCode, 'goLive') : null;

  // Full-bleed top band (not centered max-w-3xl) so the banner never sits over the
  // page title while empty space remains on the right. EnterprisePageHeader reserves
  // this height via `--admin-test-economy-chrome-height`.
  return createPortal(
    <div
      ref={wrapRef}
      className="pointer-events-none fixed inset-x-0 top-0 z-[var(--z-banner)] flex w-full flex-col isolate"
      style={{ zIndex: ADMIN_PWA_LIFECYCLE_CHROME_Z_INDEX }}
      data-testid="admin-test-economy-chrome"
      role="region"
      aria-label={t('pwa.testEconomyBanner')}
    >
      <div className="pointer-events-auto w-full border-b border-[var(--color-rail-card-border)] bg-[var(--color-an-bg)] px-[var(--space-4)] pt-2 pb-1 md:px-[var(--space-6)]">
        <OfflineBanner
          message={t('pwa.testEconomyBanner')}
          testId="admin-test-economy-banner"
          className="mb-0 max-w-none"
          leading={
            <Badge tone="warn" size="sm" data-testid="admin-test-economy-badge">
              {t('pwa.testEconomyBadge')}
            </Badge>
          }
          link={
            goLiveHref != null
              ? {
                  href: goLiveHref,
                  label: t('pwa.testEconomyGoLiveLink'),
                }
              : undefined
          }
        />
      </div>
    </div>,
    document.body,
  );
}
